import {ENVIRONMENTS, PAGES, ROUTE_NAMES, SCREEN_RESOLUTION, WIDGET_TYPES} from "../../consts";
import {ARGS} from "../../bootstrap/args";
import {action, autorun, computed, makeObservable, observable, override} from "mobx";
import {AppSettings} from "./app-settings";
import {LanguageViewModel} from "../../../domain/locale/models/language/language.view-model";
import {AppModuleConfig} from "../app-config";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {LocaleDomainModel} from "../../../domain/locale/models/locale/locale.domain-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {AnalyticsUserDataEventModel} from "../../../third-party-services/analytics/analytics-user-data-event-model";
import {BaseLinkModel} from "../../../infrastructure/models/link/base-link-model";
import {AppWizardDisplayModel} from "./app-wizard-display-model";
import {AppFollowingDisplayModel} from "./app-following-display-model";
import {AppLineupsDisplayModel} from "./app-lineups-display-model";
import {StandingsPreviewDisplayModel} from "../../models/standings-preview-display-model";
import {AppLiveStreamingDisplayModel} from "./app-live-streaming-display-model";
import {singleton} from "../../../infrastructure/decorators/singleton";
import {AppNotificationDisplayModel} from "./app-notification-display-model";
import {BookmakerViewModel} from "../../../domain/common/bookmaker/models/bookmaker-view-model";
import {ODDS_TYPES} from "../../components/odds/components/odds-consts";
import {AppModuleContext} from "./app-module.context";
import {UserAgentViewModel} from "../../models/user-agent.view-model";
import {BUZZ_URL} from "../../../apps/website/modules/main-header/main-header-module.consts";
import {EntitiesRecognizerDataSource} from "../../../domain/entities-recognizer/models/entities-recognizer.data-source";
import {AppPrivacyDisplayModel} from "./app-privacy-display-model";
import {ANALYTICS} from "../../../third-party-services/analytics/analytics-consts";
import {APP_REMOTE_CONFIG} from "../app-remote-config";
import {PoweredByModel} from "../../models/events/powered-by-model";
import {IDidomiObject} from "@didomi/react";
import {STORE_DOMAIN, THEME} from "../consts";
import {AppBettingPromotionDisplayModel} from "./app-betting-promotion-display-model";
import {AppBolaoDisplayModel} from "./app-bolao-display-model";
import {RemoteConfigManager} from "../../../third-party-services/remote-config/remote-config-manager";
import {PromotionBannerModel} from "../../models/promotion-banner/promotion-banner-model";
import {runInAction} from "mobx"
import type {RouteModel} from "../../components/router/models/route-model";
import {
  EntityDescriptionRemoteConfigModel
} from "../../../third-party-services/remote-config/models/entity-description.remote-config.model";
import {AppFaqDisplayModel} from "./app-faq-display-model";
import {BettingPromotionRemoteConfigModel} from "../../models/betting-promotion/betting-promotion.remote-config.model";
import type {BookmakerDomainModel} from "../../../domain/common/bookmaker/models/bookmaker-domain-model";
import {ENTITY_DESCRIPTION} from "../../../modules/entity-description-module/entity-description.module.consts";
import {
  PropositionBetsDetailsDisplayModel
} from "../../models/proposition-bets-details/proposition-bets.details.display-model";
import type {BolaoSettingsDataSource} from "../../../domain/bolao-settings/models/bolao-settings-data.source";
import type {StoryDomainModel} from "../../../domain/full-game/models/story/story.domain-model";
import {SessionStorageUtils} from "../../../infrastructure/utils/session-storage.utils";
import {FifthButtonRemoteConfigModel} from "../../models/fifth-button/fifth-button.remote-config.model";
import {MundialBettingButtonRemoteConfigModel} from "../../models/mundial-betting-button/mundial-betting-button.remote-config.model";
import {DynamicPopupModel} from "../../models/dynamic-popup/dynamic-popup-model";
import {PopupsDisplayManager} from "../../managers/popups-display.manager";
import {DynamicCoachmarkDisplayManager} from "../../managers/dynamic-coachmark-display.manager";
import {ExternalAdModel} from "../../models/external-ad/external-ad.model";
import {AdAdapexManager} from "../../managers/ads/ad.adapex.manager";
import {BetBoostsRemoteConfigModel} from "../../models/bet-boosts/bet-boosts.remote-config.model";
import type {BettingZoneDataSource} from "../../../domain/betting-zone/models/betting-zone-data.source";
import {GridLayoutsModel} from "../../models/grid-layout/grid-layouts.model";
import {MinuteMediaPlayerModel} from "../../models/minute-media-player/minute-media-player.model";
import {DeviceViewModel} from "../../models/device.view-model";

@singleton
export class AppModuleState extends BaseMobxState {
  env: ENVIRONMENTS;
  basename: String;
  languageChangedCallbacks: Array<Function> = [];
  @observable context: AppModuleContext
  @observable initialLinkModel: BaseLinkModel;
  @observable initialRouteModel: RouteModel;
  @observable dataInitialized: Boolean;
  settings: AppSettings;
  @observable isBackStripeVisible: Boolean;
  @observable liveGamesCount: Number;
  isUserHoverAd: Boolean;
  @observable myScoresPage: Number;
  @observable locale: LocaleDomainModel;
  @observable isHoverScreenOpen: Boolean;
  @observable showOdds: Boolean;
  @observable showSecondaryOdds: Boolean; //All odds except scores-lists
  @observable enableNotifications: Boolean;
  @observable showAllScores: Boolean;
  @observable isLive: Boolean;
  @observable userAllowedNotification: Boolean;
  @observable userPushNotificationsSubscription: String;
  @observable currentLinkModel: BaseLinkModel;
  userData: AnalyticsUserDataEventModel;
  @observable lastSelectedGameId: Number;
  @observable isAppVisible: Boolean;
  @observable isCookiesPolicyApproved: Boolean;
  @observable isSearchVisible: Boolean;
  @observable _isDebugVisible: Boolean;
  @observable _isDebugAvailable: Boolean;
  @observable sponsoredBookmaker: BookmakerViewModel;
  @observable currentOddsType: ODDS_TYPES;
  @observable entitiesRecognizerDataSource: EntitiesRecognizerDataSource;
  @observable isDarkMode: Boolean;
  @observable theme: String;
  @observable didomiObject: IDidomiObject;
  @observable gdprIncludeCountriesIds: Array<Number> = [];
  @observable notificationNoticeSessionGap: Number;
  @observable minFollowValue: Number;
  @observable promotionBannerModel: PromotionBannerModel;
  @observable entityDescriptionModel: EntityDescriptionRemoteConfigModel;
  @observable appFaqDisplayModel: AppFaqDisplayModel;
  @observable hasFAQSection: Boolean;
  @observable topBookmaker: BookmakerDomainModel;
  @observable propositionDetailsDisplay: PropositionBetsDetailsDisplayModel;
  @observable betsImpressionsBookmakersIds: Array<Number>;
  @observable bolaoSettings: BolaoSettingsDataSource;
  @observable wscFloatingStory: StoryDomainModel;
  @observable fifthButtonRemoteConfigModel: FifthButtonRemoteConfigModel;
  @observable mundialBettingButtonModel: MundialBettingButtonRemoteConfigModel;
  @observable dynamicPopupModel: DynamicPopupModel;
  @observable importantInfoStripDisplay: Boolean;
  @observable hasStripLinks: Boolean;
  @observable betBoostsRemoteConfigModel: BetBoostsRemoteConfigModel;
  @observable shouldRenderNotificationGuide: Boolean;
  @observable isBottomNavigationForAdVisible: Boolean = false;
  @observable campaign: String;
  @observable scrollToTopButtonVisible: Boolean = false;
  @observable adsPlatformType: Number;

  //Display models
  wizardDisplayModel: AppWizardDisplayModel;
  followingDisplayModel: AppFollowingDisplayModel;
  appLineupsDisplayModel: AppLineupsDisplayModel;
  standingsPreviewDisplayModel: StandingsPreviewDisplayModel;
  appLiveStreamingDisplayModel: AppLiveStreamingDisplayModel;
  appNotificationDisplayModel: AppNotificationDisplayModel;
  appPrivacyDisplayModel: AppPrivacyDisplayModel;
  appBettingPromotionDisplayModel: AppBettingPromotionDisplayModel;
  appBolaoDisplayModel: AppBolaoDisplayModel;
  bettingZoneDatasource: BettingZoneDataSource;
  @observable externalAdsModel: ExternalAdModel;
  @observable gridLayoutsModel: GridLayoutsModel;
  @observable minuteMediaPlayerModel: MinuteMediaPlayerModel;


  static injectables = [AppModuleConfig, TermsUtils, AppModuleContext, UserAgentViewModel, RemoteConfigManager, SessionStorageUtils, PopupsDisplayManager, DynamicCoachmarkDisplayManager, AdAdapexManager, DeviceViewModel];

  get isDevMode() {
    return this.env === ENVIRONMENTS.dev
  }

  get isProdMode() {
    return this.env === ENVIRONMENTS.prod
  }

  constructor(config: AppModuleConfig, termsUtils: TermsUtils, context: AppModuleContext, userAgent: UserAgentViewModel, remoteConfigManager: RemoteConfigManager, sessionStorageUtils: SessionStorageUtils, popupsDisplayManager: PopupsDisplayManager, dynamicCoachmarkDisplayManager: DynamicCoachmarkDisplayManager, adAdapexManager: AdAdapexManager, device: DeviceViewModel) {
    super();

    this.context = context;
    this.config = config;
    this.device = device;
    this.env = ARGS.NODE_ENV;
    this.basename = '';
    this.initialLinkModel = null;
    this.initialRouteModel = null;
    this.dataInitialized = false;
    this.isUserHoverAd = false;
    this.liveGamesCount = 0;
    this.myScoresPage = 0;
    this.isBackStripeVisible = false;
    this.isHoverScreenOpen = false;
    this.showAllScores = false;
    this.isLive = false;
    this.termsUtils = termsUtils;
    this.userPushNotificationsSubscription = null;
    this.wizardDisplayModel = new AppWizardDisplayModel();
    this.followingDisplayModel = new AppFollowingDisplayModel();
    this.isSearchVisible = false;
    this.appLineupsDisplayModel = new AppLineupsDisplayModel();
    this.appLiveStreamingDisplayModel = new AppLiveStreamingDisplayModel();
    this.appNotificationDisplayModel = new AppNotificationDisplayModel(userAgent);
    this.isAppVisible = true;
    this.standingsPreviewDisplayModel = new StandingsPreviewDisplayModel();
    this.appPrivacyDisplayModel = new AppPrivacyDisplayModel();
    this.appBettingPromotionDisplayModel = new AppBettingPromotionDisplayModel();
    this.fifthButtonRemoteConfigModel = new FifthButtonRemoteConfigModel({});
    this.mundialBettingButtonModel = new MundialBettingButtonRemoteConfigModel({});
    this.appBolaoDisplayModel = new AppBolaoDisplayModel();
    this.isDarkMode = false;
    this.theme = THEME.LIGHT;
    this.didomiObject = null;
    this.entityDescriptionModel = new EntityDescriptionRemoteConfigModel({}, sessionStorageUtils);
    this.appFaqDisplayModel = new AppFaqDisplayModel();
    this.propositionDetailsDisplay = new PropositionBetsDetailsDisplayModel();
    this.betsImpressionsBookmakersIds = new Map();
    this.wscFloatingStory = null;
    this.dynamicPopupModel = null;
    this.importantInfoStripDisplay = false;
    this.popupsDisplayManager = popupsDisplayManager;
    this.hasStripLinks = false;
    this.externalAdsModel = new ExternalAdModel();
    this.dynamicCoachmarkDisplayManager = dynamicCoachmarkDisplayManager;
    this.adAdapexManager = adAdapexManager;
    this.betBoostsRemoteConfigModel = new BetBoostsRemoteConfigModel();
    this.shouldRenderNotificationGuide = false;
    this.minuteMediaPlayerModel = new MinuteMediaPlayerModel({});

    makeObservable(this);

    remoteConfigManager.registerToInitCallback(() => runInAction(() => {
      this.entityDescriptionModel = new EntityDescriptionRemoteConfigModel(this.config.getValue(APP_REMOTE_CONFIG.ENTITY_DESCRIPTION_CONFIG), sessionStorageUtils)
      this.minFollowValue = this.config.getValue(APP_REMOTE_CONFIG.MINIMUM_FOLLOWING_TO_DISPLAY);
      this.notificationNoticeSessionGap = this.config.getValue(APP_REMOTE_CONFIG.NOTIFICATIONS_NOTICE_SESSION_GAP);
      this.gdprIncludeCountriesIds = this.config.getValue(APP_REMOTE_CONFIG.GDPR_INCLUDES_COUNTRIES)?.convertToNumberArray() || [];
      this.promotionBannerModel = new PromotionBannerModel(this.config.getValue(APP_REMOTE_CONFIG.PROMOTION_BANNER));
      this.fifthButtonRemoteConfigModel = new FifthButtonRemoteConfigModel(this.config.getValue(APP_REMOTE_CONFIG.FIFTH_BUTTON));
      this.mundialBettingButtonModel = new MundialBettingButtonRemoteConfigModel(this.config.getValue(APP_REMOTE_CONFIG.MUNDIAL_BETTING_BUTTON_CONFIG));
      this.betBoostsRemoteConfigModel = new BetBoostsRemoteConfigModel(this.config.getValue(APP_REMOTE_CONFIG.BET_BOOSTS_CONFIGURATION));
      this.popupsDisplayManager.init(this.config.getValue(ARGS.DEV_ENVIRONMENT ? APP_REMOTE_CONFIG.POPUP_CONFIG_TEST : APP_REMOTE_CONFIG.AUTO_DISPLAY_POPUP_CONFIG));
      this.dynamicCoachmarkDisplayManager.init(this.config.getValue(APP_REMOTE_CONFIG.DYNAMIC_COACHMARKS));
      this.gridLayoutsModel = new GridLayoutsModel(this.config.getValue(APP_REMOTE_CONFIG.GRID_LAYOUT_CONFIG));
      this.minuteMediaPlayerModel = new MinuteMediaPlayerModel(this.config.getValue(APP_REMOTE_CONFIG.MINUTE_MEDIA_PLAYER));

      autorun(() => {
        const dynamicPopupModel = this.config.getValue(ARGS.DEV_ENVIRONMENT ? APP_REMOTE_CONFIG.WEB_DYNAMIC_POPUP_TEST : APP_REMOTE_CONFIG.WEB_DYNAMIC_POPUP);
        this.appBettingPromotionDisplayModel.updateRemoteConfigModel(new BettingPromotionRemoteConfigModel(this.config.getValue(APP_REMOTE_CONFIG.BETTING_PROMOTION), this.terms, this.topBookmakerId));
        this.updateDynamicPopupModel(Array.isArray(dynamicPopupModel) ? dynamicPopupModel?.map((model) => new DynamicPopupModel(model, this.terms, this.language)) : [new DynamicPopupModel(dynamicPopupModel, this.terms, this.language)]);
      });
    }))
  }

  @override get isPopulated(): Boolean {
    return true;
  }

  @computed get language(): LanguageViewModel {
    return new LanguageViewModel(this.locale?.language);
  }

  @computed get showBackStripe() {
    return this.isBackStripeVisible && !this.isHoverScreenOpen;
  }

  get showPoweredByButton(): String {
    const showPoweredByButtonBySettings = Boolean(this.settings.bootstrapSettings.showPoweredBy);
    const showPoweredByButtonByRoute = ![ROUTE_NAMES.sliderPage, ROUTE_NAMES.topGames].includes(this.currentLinkModel?.name);

    return showPoweredByButtonBySettings && showPoweredByButtonByRoute;
  }

  @computed get shouldRenderSponsorship(): Boolean {
    const disableSponsorshipRender = [WIDGET_TYPES.slider, WIDGET_TYPES.predictions].includes(this.settings.widgetType);
    const shouldRenderSponsorship: Boolean = !disableSponsorshipRender && this.settings.isWidget && !!this.sponsoredBookmaker && this.settings.bootstrapSettings.showSponsorship;

    return shouldRenderSponsorship;
  }

  @computed get shouldRenderSponsorshipPoweredBy(): Boolean {
    const shouldRenderSponsorshipPoweredBy: Boolean = this.settings.isWidget && this.settings.bootstrapSettings.showPoweredBy;

    return shouldRenderSponsorshipPoweredBy;
  }

  @computed get shouldRenderBottomPoweredBy(): Boolean {
    const isUnavailableRoutes: Boolean = [ROUTE_NAMES.sliderPage].includes(this.initialLinkModel?.name);
    const shouldRenderBottomPoweredBy: Boolean = this.settings.isWidget && this.settings.bootstrapSettings.showPoweredBy && !this.shouldRenderSponsorship && !isUnavailableRoutes;

    return shouldRenderBottomPoweredBy;
  }

  @computed get shouldRenderSponsorshipWrapper(): Boolean {
    return this.shouldRenderSponsorship || this.shouldRenderSponsorshipPoweredBy;
  }

  @computed get sponsorshipUrl(): String {
    const sponsorshipUrl: String = this.settings.publisherSettings?.bookmakerUrl || this.sponsoredBookmaker?.link;

    return sponsorshipUrl;
  }

  set isDebugVisible(visible: Boolean) {
    this._isDebugVisible = visible;
  }

  @computed get isDebugVisible(): Boolean {
    let isDebugVisible = this._isDebugVisible;

    return isDebugVisible && this.isDebugAvailable;
  }

  @computed get isDebugAvailable(): Boolean {
    return this._isDebugAvailable || ARGS.EXPOSE_DEBUG === 'true';
  }

  get shouldRenderBuzzItem(): Boolean {
    const currentBuzzItem = BUZZ_URL[this.language.displayName];
    const validLang: Boolean = currentBuzzItem?.url;
    const validLocation: Boolean = !currentBuzzItem?.showOnlyInFooter;
    const shouldRenderBuzzItem: Boolean = validLang && validLocation;

    return shouldRenderBuzzItem
  }

  @computed get shouldWidgetOverflow(): Boolean {
    const shouldWidgetOverflow: Boolean = this.settings.bootstrapSettings.limitHeightDisplay > 0 && !this.settings.useScrollingButtons

    return shouldWidgetOverflow;
  }

  @computed get hasSponsorship(): Boolean {
    return !!this.settings.publisherSettings?.id && !!this.sponsoredBookmaker?.id && this.settings.bootstrapSettings.showSponsorship;
  }

  @computed get poweredByModel(): PoweredByModel {
    return new PoweredByModel(this.terms)
  }

  @computed get shouldSupportConsent(): Boolean {
    const isCampaignLink = this.appBettingPromotionDisplayModel.isCampaignLink;
    const countriesIds: Array<Number> = this.gdprIncludeCountriesIds;

    const isValidCountryId: Boolean = !this.settings?.isWidget && isCampaignLink === false && countriesIds.includes(this.locale?.userCountryId)

    return isValidCountryId
  }

  get didomiVendorsIds(): String {
    const didomiVendorsIds: String = Object.values(ANALYTICS.VENDORS_ID).toString();

    return didomiVendorsIds
  }

  @computed get shouldDisableGameRoute(): Boolean {
    const gamesRoutes: Array<String> = [ROUTE_NAMES.game];
    const shouldDisableGameRoute: Boolean = !this.settings.isWidget && !gamesRoutes.includes(this.currentLinkModel.name)

    return shouldDisableGameRoute;
  }

  get showDisclaimer(): Boolean {
    return this.settings.bootstrapSettings.showDisclaimer;
  }

  get numberOfOutrights(): Boolean {
    return this.settings.bootstrapSettings.numberOfOutrights;
  }

  get numberOfMaxGamesForBestOdds(): Boolean {
    return this.settings.bootstrapSettings.maxGamesBestOdds;
  }

  get numberOfMinGamesForBestOdds(): Boolean {
    return this.settings.bootstrapSettings.minGamesBestOdds;
  }

  get numberOfShowCollapsedGamesForBestOdds(): Boolean {
    return this.settings.bootstrapSettings.numberOfShowCollapsedGamesForBestOdds;
  }

  get shouldRenderBestOddsCompetitionFilter(): Boolean {
    return this.settings.bootstrapSettings.addCompetitionFilterForBestOdds;
  }

  get seeMoreDisplay(): Boolean {
    return this.settings.bootstrapSettings.seeMoreDisplay;
  }

  @computed get selectedGameId(): Number {
    const currentGameId: Number = this.currentLinkModel?.hashParams?.id;
    const currentNumberGameId: Number = currentGameId && Number(currentGameId);
    const selectedGameId: Number = this.context.game?.id || currentNumberGameId;

    return selectedGameId;
  }

  @action setWscFloatingStory(story: StoryDomainModel) {
    this.wscFloatingStory = story || null;
  }

  @computed get isGameCenterPage(): Boolean {
    return [ROUTE_NAMES.gameCenterPage, Object.values(PAGES.GAME_CENTER)].flat()?.includes((this.currentLinkModel?.name))
  }

  @computed get shouldRenderWscFloatingButton(): Boolean {
    return !!this.wscFloatingStory && this.isGameCenterPage;
  }

  @computed get showBolao(): Boolean {
    return this.bolaoSettings?.isAvailable && this.appBolaoDisplayModel.isVisible;
  }


  @computed get showBolaoFloating(): Boolean {
    return this.bolaoSettings?.isAvailable && this.appBolaoDisplayModel.isFloatingVisible;
  }

  @computed get showBolaoNavigation(): Boolean {
    return this.bolaoSettings?.isAvailable && this.appBolaoDisplayModel.isNavigationsVisible;
  }

  @computed get shouldRenderAdsBorders(): Boolean {
    return parseInt(this.device.screenWidth) >= SCREEN_RESOLUTION.sky_scrapper;
  }

  @computed get shouldRenderEntityDescription(): Boolean {
    return this.entityDescriptionModel?.isValid(this.context.sportType?.id, null, null, this.language?.id, this.locale?.userCountryId, ENTITY_DESCRIPTION.ENTITY_TYPE.sport);
  }

  get isStoreDomain() {
    return window.location.host === STORE_DOMAIN;
  }

  @computed get topBookmakerId() {
    return this.topBookmaker?.id;
  }

  get containerLimitHeight() {
    return (this.settings.bootstrapSettings.limitHeightDisplay - 42) + "px";
  }

  @action updateTheme(theme) {
    this.theme = theme
  }

  @action updateTopBookmaker(topBookmaker) {
    this.topBookmaker = topBookmaker
  }

  @action updateHasStripLinks(value) {
    this.hasStripLinks = value
  }

  @action updateDynamicPopupModel(model) {
    this.dynamicPopupModel = model
  }

  @computed get isBettingPage(): Boolean {
    const bettingPages: Array<ROUTE_NAMES> = [ROUTE_NAMES.betting, ...(Object.values(PAGES.BETTING))];

    return bettingPages.includes(this.currentLinkModel?.name);
  }

  @computed get shouldRenderBettingButton(): Boolean {
    return this.locale.isBettingZoneValidByLangAndCountry;
  }

  @computed get shouldRenderOddsByPromotions() {
    return this.locale.isBettingZoneValidByLangAndCountry && (this.locale.userCountryId !== 21);
  }
}
